import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';
import { Building2, User, Phone, Mail, MapPin, Package, FileText, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VENDOR_CATEGORIES = {
  'Restaurant & Hospitality': [
    'Restaurant owners',
    'Hotels (3-5 star with central kitchens)',
    'Standalone restaurants (tandoor, barbecue, mandi, grills)',
    'QSR chains and franchise brands',
    'Caterers and industrial canteens',
    'Central kitchens (Swiggy/Zomato brands)',
  ],
  'Accommodation': [
    'Paying Guest Owners',
    'Hostels and dormitories',
  ],
  'Domestic': [
    'Domestic Household cooking',
    'Rural households and village clusters',
  ],
  'Core Industrial': [
    'Core industrial buyers',
    'Cement plants and mini-cement grinding units',
    'Steel re-rolling mills and foundries',
    'Textile, dyeing, and garment washing units',
    'Food processing units (dairies, oil mills, rice mills)',
    'Brick kilns and tile factories',
    'Biomass power plants',
  ],
  'Commercial Kitchens': [
    'Commercial kitchens and hospitality',
    'Bakeries and small-scale ovens',
    'Dairy farms (paneer, ghee units)',
    'Tea estates and spice drying units',
  ],
  'Traders & Brokers': [
    'Biomass briquette wholesalers',
    'Biomass buyers and brokers',
    'Exporters and importers',
    'Government and institutional buyers',
  ],
  'Online & Direct': [
    'Online B2B marketplace operators',
    'WhatsApp group vendors',
    'Street-food vendors and tiffin centers',
  ],
};

const VendorEmpanelmentPage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({
    business_name: '',
    contact_person: '',
    phone: '',
    email: '',
    vendor_category: '',
    business_type: '',
    monthly_requirement_kg: '',
    delivery_location: '',
    address: '',
    additional_notes: '',
  });

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    setFormData({ ...formData, vendor_category: category, business_type: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        monthly_requirement_kg: formData.monthly_requirement_kg ? parseInt(formData.monthly_requirement_kg) : null,
      };

      await axios.post(`${API}/vendor-empanelment`, submitData);
      toast.success('Vendor empanelment request submitted successfully! We will contact you soon.');
      
      setFormData({
        business_name: '',
        contact_person: '',
        phone: '',
        email: '',
        vendor_category: '',
        business_type: '',
        monthly_requirement_kg: '',
        delivery_location: '',
        address: '',
        additional_notes: '',
      });
      setSelectedCategory('');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-5xl py-16">
        <div className="mb-12 text-center">
          <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-4 font-heading">
            Vendor <span className="text-lime-400">Empanelment</span>
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-300 max-w-3xl mx-auto mb-6">
            Join GreenPure Biofuel's growing network of partners. Register your business for bulk supply of premium biomass briquettes.
          </p>
          <a
            href="https://wa.me/918431598801?text=Hi%2C%20I%20want%20to%20register%20as%20a%20vendor"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-6 py-3 transition-colors"
            data-testid="whatsapp-vendor-cta"
          >
            <MessageCircle className="w-5 h-5" />
            Quick Inquiry on WhatsApp
          </a>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="border-b border-zinc-800 pb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2 text-lime-400">
                <Building2 className="w-6 h-6" />
                Business Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="input-business-name"
                    placeholder="Enter your business name"
                  />
                </div>
                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    <User className="inline w-4 h-4 mr-1" /> Contact Person *
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="input-contact-person"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    <Phone className="inline w-4 h-4 mr-1" /> Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="input-phone"
                    placeholder="10-digit mobile number"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    <Mail className="inline w-4 h-4 mr-1" /> Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="input-email"
                    placeholder="business@email.com"
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-zinc-800 pb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2 text-lime-400">
                <Package className="w-6 h-6" />
                Business Category
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    Vendor Category *
                  </label>
                  <select
                    name="vendor_category"
                    value={formData.vendor_category}
                    onChange={handleCategoryChange}
                    required
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="select-vendor-category"
                  >
                    <option value="">Select Category</option>
                    {Object.keys(VENDOR_CATEGORIES).map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCategory && (
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                      Business Type *
                    </label>
                    <select
                      name="business_type"
                      value={formData.business_type}
                      onChange={handleChange}
                      required
                      className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                      data-testid="select-business-type"
                    >
                      <option value="">Select Business Type</option>
                      {VENDOR_CATEGORIES[selectedCategory].map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    Monthly Requirement (kg)
                  </label>
                  <input
                    type="number"
                    name="monthly_requirement_kg"
                    value={formData.monthly_requirement_kg}
                    onChange={handleChange}
                    min="0"
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="input-monthly-requirement"
                    placeholder="Estimated monthly requirement in kg"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Optional - helps us serve you better</p>
                </div>
              </div>
            </div>

            <div className="border-b border-zinc-800 pb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2 text-lime-400">
                <MapPin className="w-6 h-6" />
                Delivery Location
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    Delivery Location (Pincode) *
                  </label>
                  <input
                    type="text"
                    name="delivery_location"
                    value={formData.delivery_location}
                    onChange={handleChange}
                    required
                    maxLength={6}
                    pattern="[0-9]{6}"
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                    data-testid="input-delivery-location"
                    placeholder="6-digit pincode for delivery"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Enter the pincode where you want deliveries</p>
                </div>
                <div>
                  <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                    Complete Address *
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 text-lg px-4 py-3 text-white border outline-none"
                    data-testid="input-address"
                    placeholder="Full business address with landmarks"
                  />
                </div>
              </div>
            </div>

            <div className="pb-6">
              <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2 text-lime-400">
                <FileText className="w-6 h-6" />
                Additional Information
              </h2>
              <div>
                <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                  Additional Notes
                </label>
                <textarea
                  name="additional_notes"
                  value={formData.additional_notes}
                  onChange={handleChange}
                  rows={4}
                  className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 text-lg px-4 py-3 text-white border outline-none"
                  data-testid="input-additional-notes"
                  placeholder="Any specific requirements, delivery preferences, or questions"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-vendor-form"
              >
                {loading ? 'Submitting...' : 'Submit Empanelment Request'}
              </button>
            </div>

            <div className="bg-lime-400/10 border border-lime-400/20 p-4 mt-4">
              <p className="text-lime-400 font-semibold text-sm mb-1">✓ We'll review your application</p>
              <p className="text-zinc-400 text-xs">Our team will contact you within 24-48 hours to discuss partnership details</p>
            </div>
          </form>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
            <Package className="w-10 h-10 text-lime-400 mx-auto mb-3" />
            <h3 className="font-semibold text-white mb-2">Bulk Pricing</h3>
            <p className="text-zinc-400 text-sm">Competitive rates for high-volume orders</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
            <MapPin className="w-10 h-10 text-lime-400 mx-auto mb-3" />
            <h3 className="font-semibold text-white mb-2">Zone-wise Delivery</h3>
            <p className="text-zinc-400 text-sm">Assembly-level dark stores across Bengaluru</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
            <Phone className="w-10 h-10 text-lime-400 mx-auto mb-3" />
            <h3 className="font-semibold text-white mb-2">Dedicated Support</h3>
            <p className="text-zinc-400 text-sm">+91-8431598801</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VendorEmpanelmentPage;
